import { X } from 'lucide-react';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { SearchLoop } from 'assets/icons';
import useCloseOnEscape from 'components/crm/ui/useCloseOnEscape';
import PickOption from 'components/record/PickOption';
import { isDeletedGroupLink, useLinkLabel } from 'components/record/deletedGroup';
import StyledInput from 'components/styledInput/StyledInput';
import { LINK_TYPES, linkKey, linkOptionsOf } from 'helper/taskLinks';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import crmApi from 'server/crm/crmApi';
import { isValidGuestEmail } from 'components/planning/withWhom';
import LINK_ICONS from '../linkIcons';

// The search's hint in the task form and in the task panel.
export const LINKS_PLACEHOLDER = 'Rechercher une entreprise, un contact ou un intérimaire';
// A long list is cut: the search narrows it.
const MAX_PER_TYPE = 20;
const MIN_SEARCH = 2;
const SEARCH_DELAY_MS = 250;
const folded = (value) =>
  (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// The kinds of « Avec qui ? » (components/planning/withWhom), French in the code.
const GROUP_LABELS = { colleague: 'Collègues', email: 'Adresse e-mail' };

/**
 * « Fiches liées »: the records of a task, as many as needed, each a chip
 * under the search bar with a cross that removes it. The search (the bar and
 * the options of « Créer une mission » on a group), on the name only, opens the
 * options grouped by kind (companies, groups, contacts, temp workers); a
 * temp worker not validated yet says so. Temp workers are too many to be
 * listed: from 2 letters typed, they are searched in the api. `value`: the links; `options`: the
 * links that can be added (helper/taskLinks `linkOptionsOf`). `withTempWorkers`
 * false (a note): no temp worker is searched nor offered.
 */
const TaskLinksField = ({
  label,
  hint,
  placeholder,
  options,
  value,
  onChange,
  withTempWorkers = true,
  // The list opens above the field (the last of a form), or below it.
  openUpwards = true,
  // The kinds offered, in this order.
  types = LINK_TYPES,
  // An address typed is offered as it is (an appointment's guest).
  allowEmails = false,
}) => {
  const { text } = useText('taskForm');
  const linkLabel = useLinkLabel();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // The temp workers found, with the search they answer: once the search has
  // changed, they no longer match what is typed and are not offered.
  const [found, setFound] = useState({ needle: '', items: [] });
  const [searching, setSearching] = useState(false);
  const { call } = useCrmContext();
  const box = useRef(null);
  const latestSearch = useRef(0);
  const needle = query.trim();
  useEffect(() => {
    const request = ++latestSearch.current;
    if (!withTempWorkers || needle.length < MIN_SEARCH) {
      setFound({ needle: '', items: [] });
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      call(crmApi.getTaskTempWorkers, { search: needle }).then(
        (workers) => {
          // The answer to an older keystroke is out of date.
          if (request !== latestSearch.current) return;
          setFound({ needle, items: linkOptionsOf([], workers) });
          setSearching(false);
        },
        () => {
          if (request !== latestSearch.current) return;
          setFound({ needle, items: [] });
          setSearching(false);
        },
      );
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [needle, call, withTempWorkers]);
  // Closed, the search empties: what was typed served that list only.
  const closeList = () => {
    setOpen(false);
    setQuery('');
  };
  useEffect(() => {
    const close = (event) => box.current && !box.current.contains(event.target) && closeList();
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  useCloseOnEscape(open, closeList);

  const taken = useMemo(() => new Set(value.map(linkKey)), [value]);
  // A company ticked unfolds its people under it, to tick as well.
  const openCompanies = useMemo(
    () => new Set(value.filter((link) => link.type === 'company').map((link) => link.id)),
    [value],
  );
  const groups = useMemo(() => {
    const tempWorkers = found.needle === needle ? found.items : [];
    const search = folded(query.trim());
    const left = [...options, ...tempWorkers].filter(
      (option) =>
        (withTempWorkers || option.type !== 'tempWorker') &&
        // The api already matched the temp workers, on first or last name.
        (option.type === 'tempWorker' || !search || folded(`${option.label} ${option.email ?? ''} ${option.legalName ?? ''}`).includes(search)),
    );
    // An address nobody known has: offered alone, to invite it as it is.
    const email = query.trim().toLowerCase();
    if (
      allowEmails &&
      isValidGuestEmail(email) &&
      !options.some((option) => option.email === email) &&
      !value.some((link) => link.email === email)
    )
      left.push({ type: 'email', id: email, label: email, sub: 'Inviter cette adresse', email });
    const byType = (type, from) => from.filter((option) => option.type === type).slice(0, MAX_PER_TYPE);
    // The people of a company ticked and listed are drawn under it, not apart.
    const unfolded = new Set(byType('company', left).filter((company) => openCompanies.has(company.id)).map((company) => company.id));
    const shown = left.filter((option) => !(option.type === 'contact' && unfolded.has(option.companyId)));
    return types.map((type) => ({ type, items: byType(type, shown) })).filter((group) => group.items.length);
  }, [options, found, needle, query, withTempWorkers, openCompanies, types, allowEmails, value]);
  const peopleOf = (companyId) =>
    options.filter((option) => option.type === 'contact' && option.companyId === companyId);

  // Ticked or unticked, the list stays open: several are picked in a row. A
  // company unticked takes its people with it. A link `locked` stays.
  const toggle = (option) => {
    if (value.some((link) => link.locked && linkKey(link) === linkKey(option))) return;
    if (!taken.has(linkKey(option))) {
      onChange([...value, option]);
      return;
    }
    onChange(
      value.filter(
        (link) =>
          linkKey(link) !== linkKey(option) &&
          !(option.type === 'company' && link.type === 'contact' && link.companyId === option.id),
      ),
    );
  };
  const remove = (link) =>
    onChange(value.filter((other) => linkKey(other) !== linkKey(link)));

  return (
    <div className="flex flex-col gap-1.5">
      {/* The search and its list only: the list hangs right under the field,
          and a click anywhere else, the chips included, closes it. */}
      <div ref={box} className="relative">
        <StyledInput
          type="text"
          label={label}
          hint={hint}
          value={query}
          placeholder={placeholder}
          imgBack={<SearchLoop wh={16} color="#6B7280" />}
          role="combobox"
          ariaExpanded={open}
          ariaControls={`${id}-list`}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          // Enter adds the address typed, without sending the form.
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || !allowEmails) return;
            event.preventDefault();
            const typed = groups.find((group) => group.type === 'email')?.items[0];
            if (typed) {
              toggle(typed);
              setQuery('');
            }
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
        />
        {open && (
          <div
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            className={`absolute left-0 right-0 z-30 max-h-72 overflow-y-auto rounded-xl border border-gray-100 bg-white p-2 shadow-lg motion-safe:animate-contentIn ${
              openUpwards ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          >
            {groups.map((group) => (
              <div
                key={group.type}
                role="group"
                aria-label={GROUP_LABELS[group.type] ?? text(`linksGroup_${group.type}`)}
              >
                <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                  {GROUP_LABELS[group.type] ?? text(`linksGroup_${group.type}`)}
                </p>
                {group.items.map((option) => (
                  <Fragment key={linkKey(option)}>
                    <PickOption
                      role="option"
                      ariaSelected={taken.has(linkKey(option))}
                      checked={taken.has(linkKey(option))}
                      icon={LINK_ICONS[option.type]}
                      name={option.label}
                      detail={option.sub}
                      // Keeps the focus in the search, so that several are added in a row.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => toggle(option)}
                    />
                    {option.type === 'company' &&
                      openCompanies.has(option.id) &&
                      peopleOf(option.id).map((person) => (
                        <PickOption
                          key={linkKey(person)}
                          indented
                          role="option"
                          ariaSelected={taken.has(linkKey(person))}
                          checked={taken.has(linkKey(person))}
                          icon={LINK_ICONS.contact}
                          name={person.label}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => toggle(person)}
                        />
                      ))}
                  </Fragment>
                ))}
              </div>
            ))}
            {!withTempWorkers ? null : needle.length < MIN_SEARCH ? (
              <p className="px-2 py-2 text-xs text-gray-400">{text('tempWorkersHint')}</p>
            ) : (
              searching && <p className="px-2 py-2 text-xs text-gray-400">{text('tempWorkersSearching')}</p>
            )}
            {!groups.length && !searching && (
              <p className="px-2 py-3 text-sm text-gray-500">
                {text('contactNoResult')}
              </p>
            )}
          </div>
        )}
      </div>
      {value.length > 0 && (
        // A chip per record: its icon, its name, the cross that removes it.
        <div className="flex flex-wrap gap-2">
          {value.map((link) => {
            const Icon = LINK_ICONS[link.type];
            return (
              <span
                key={linkKey(link)}
                className="inline-flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-white py-1.5 pl-2.5 pr-1.5 text-sm leading-5 text-gray-800"
              >
                <span className="flex shrink-0 text-gray-500">
                  <Icon wh={14} color="currentColor" />
                </span>
                <span className={`max-w-[14rem] truncate ${isDeletedGroupLink(link) ? 'text-gray-400' : ''}`}>{linkLabel(link)}</span>
                {!link.locked && (
                  <button
                    type="button"
                    aria-label={text('linksRemove', { label: link.label })}
                    onClick={() => remove(link)}
                    className="flex size-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TaskLinksField;
